import { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Grid, Card, CardContent, CardActionArea,
  Chip, IconButton, Alert, Skeleton, Tabs, Tab, InputAdornment, LinearProgress, Fab,
} from '@mui/material';
import {
  Plus, Eye, LogIn, LogOut as LogOutIcon, X as XIcon, Banknote, Smartphone, CreditCard, DollarSign, FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { bookingApi, invoiceApi, Booking, Room, getDisplayAmount, getPaymentStatus } from '../../services/hotelApi';

const STATUS_TABS = ['pending', 'checked-in', 'checked-out', 'cancelled'] as const;
const STATUS_COLORS: Record<string, string> = { pending: '#ff9800', 'checked-in': '#4caf50', 'checked-out': '#42a5f5', cancelled: '#f44336' };
const PM_ICONS: Record<string, any> = { cash: Banknote, mobile_money: Smartphone, bank_transfer: CreditCard, card: CreditCard };

const Bookings = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIdx, setTabIdx] = useState(0);

  // Detail / actions
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [openActions, setOpenActions] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Payment dialog
  const [openPay, setOpenPay] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<string>('cash');

  const isFr = language === 'fr';
  const currency = user?.company?.signCurrency || '$';
  const basePath = `/${language}/hotel-dashboard`;

  const t = isFr ? {
    title: 'Réservations', subtitle: 'Gérer les réservations de votre hôtel',
    pending: 'En attente', checkedIn: 'Arrivé', checkedOut: 'Parti', cancelled: 'Annulé',
    newBooking: 'Nouvelle réservation', noBookings: 'Aucune réservation dans cet onglet',
    client: 'Client', room: 'Chambre', phone: 'Tél', checkIn: 'Arrivée', checkOut: 'Départ',
    amount: 'Montant', paid: 'Payé', remaining: 'Reste', wife: 'Ép.',
    doCheckIn: 'Check-In', doCheckOut: 'Check-Out', doCancel: 'Annuler',
    doPay: 'Payer', generateInvoice: 'Générer facture',
    payTitle: 'Enregistrer un paiement', payAmount: 'Montant', payMethod: 'Méthode',
    cash: 'Espèces', mobile_money: 'Mobile Money', bank_transfer: 'Virement', card: 'Carte',
    confirm: 'Confirmer', cancel: 'Annuler', close: 'Fermer',
    confirmCancel: 'Êtes-vous sûr de vouloir annuler cette réservation ?',
    confirmCheckIn: 'Confirmer le check-in ?', confirmCheckOut: 'Confirmer le check-out ?',
    negotiated: 'Tarif négocié', paidLabel: 'Payé', unpaid: 'Impayé', partial: 'Partiel', noAmount: 'N/A',
    errorLoading: 'Erreur lors du chargement',
  } : {
    title: 'Bookings', subtitle: 'Manage your hotel bookings',
    pending: 'Pending', checkedIn: 'Checked In', checkedOut: 'Checked Out', cancelled: 'Cancelled',
    newBooking: 'New Booking', noBookings: 'No bookings in this tab',
    client: 'Client', room: 'Room', phone: 'Phone', checkIn: 'Check-In', checkOut: 'Check-Out',
    amount: 'Amount', paid: 'Paid', remaining: 'Remaining', wife: 'Sp.',
    doCheckIn: 'Check-In', doCheckOut: 'Check-Out', doCancel: 'Cancel',
    doPay: 'Pay', generateInvoice: 'Generate Invoice',
    payTitle: 'Record a Payment', payAmount: 'Amount', payMethod: 'Method',
    cash: 'Cash', mobile_money: 'Mobile Money', bank_transfer: 'Bank Transfer', card: 'Card',
    confirm: 'Confirm', cancel: 'Cancel', close: 'Close',
    confirmCancel: 'Are you sure you want to cancel this booking?',
    confirmCheckIn: 'Confirm check-in?', confirmCheckOut: 'Confirm check-out?',
    negotiated: 'Negotiated rate', paidLabel: 'Paid', unpaid: 'Unpaid', partial: 'Partial', noAmount: 'N/A',
    errorLoading: 'Error loading data',
  };

  const tabLabels = [t.pending, t.checkedIn, t.checkedOut, t.cancelled];

  useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    try {
      setLoading(true); setError('');
      const data = await bookingApi.getAll();
      setBookings(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || t.errorLoading);
    } finally { setLoading(false); }
  };

  const currentStatus = STATUS_TABS[tabIdx];
  const filtered = bookings.filter(b => b.status === currentStatus);
  const tabCounts = STATUS_TABS.map(s => bookings.filter(b => b.status === s).length);

  const getRoomNum = (b: Booking) => (b.roomId && typeof b.roomId === 'object') ? (b.roomId as Room).roomNumber ?? '-' : '-';
  const getRoomType = (b: Booking) => {
    if (b.roomId && typeof b.roomId === 'object') {
      const r = b.roomId as Room;
      if (r.roomTypeId && typeof r.roomTypeId === 'object') return (r.roomTypeId as any).name;
    }
    return '';
  };

  const fmtDate = (d?: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString(isFr ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fmtPayment = (m?: string) => {
    if (!m) return '-';
    const map: Record<string, string> = { cash: t.cash, mobile_money: t.mobile_money, bank_transfer: t.bank_transfer, card: t.card };
    return map[m] || m;
  };

  const pmStatusLabel = (s: string) => {
    const map: Record<string, string> = { paid: t.paidLabel, partial: t.partial, unpaid: t.unpaid, 'no-amount': t.noAmount };
    return map[s] || s;
  };
  const pmStatusColor = (s: string) => {
    const map: Record<string, string> = { paid: '#4caf50', partial: '#ff9800', unpaid: '#f44336', 'no-amount': '#9e9e9e' };
    return map[s] || '#9e9e9e';
  };

  // Actions
  const handleCheckIn = async (id: string) => {
    if (!window.confirm(t.confirmCheckIn)) return;
    try { setActionLoading(true); await bookingApi.checkIn(id); setOpenActions(false); await loadBookings(); } catch (e: any) { setError(e.message); } finally { setActionLoading(false); }
  };
  const handleCheckOut = async (id: string) => {
    if (!window.confirm(t.confirmCheckOut)) return;
    try { setActionLoading(true); await bookingApi.checkOut(id); setOpenActions(false); await loadBookings(); } catch (e: any) { setError(e.message); } finally { setActionLoading(false); }
  };
  const handleCancel = async (id: string) => {
    if (!window.confirm(t.confirmCancel)) return;
    try { setActionLoading(true); await bookingApi.cancel(id); setOpenActions(false); await loadBookings(); } catch (e: any) { setError(e.message); } finally { setActionLoading(false); }
  };
  const handlePay = async () => {
    if (!selectedBooking || !payAmount) return;
    try {
      setActionLoading(true);
      await bookingApi.pay(selectedBooking._id, Number(payAmount), payMethod as any);
      setOpenPay(false); setOpenActions(false);
      await loadBookings();
    } catch (e: any) { setError(e.message); } finally { setActionLoading(false); }
  };
  const handleGenerateInvoice = async (id: string) => {
    try {
      setActionLoading(true);
      await invoiceApi.create(id);
      setOpenActions(false);
      navigate(`${basePath}/${isFr ? 'factures' : 'invoices'}`);
    } catch (e: any) { setError(e.message); } finally { setActionLoading(false); }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{t.title}</Typography>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={18} />}
          onClick={() => navigate(`${basePath}/${isFr ? 'nouvelle-reservation' : 'new-booking'}`)}
          sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>
          {t.newBooking}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* 4 Status Tabs */}
      <Tabs value={tabIdx} onChange={(_, v) => setTabIdx(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        {tabLabels.map((label, i) => (
          <Tab key={i} label={`${label} (${tabCounts[i]})`} sx={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => <Grid item xs={12} sm={6} md={4} key={i}><Skeleton variant="rounded" height={180} /></Grid>)}
        </Grid>
      ) : filtered.length === 0 ? (
        <Alert severity="info">{t.noBookings}</Alert>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(b => {
            const displayAmt = getDisplayAmount(b);
            const pStatus = getPaymentStatus(b);
            const paid = b.paidAmount || 0;
            const remain = b.remainingAmount || 0;
            const pct = displayAmt > 0 ? Math.min((paid / displayAmt) * 100, 100) : 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={b._id}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <CardActionArea onClick={() => { setSelectedBooking(b); setOpenActions(true); }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{b.clientName}</Typography>
                          {b.nomFemme && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t.wife} {b.nomFemme}</Typography>}
                        </Box>
                        <Chip label={pmStatusLabel(pStatus)} size="small" sx={{ backgroundColor: `${pmStatusColor(pStatus)}15`, color: pmStatusColor(pStatus), fontWeight: 600, fontSize: '0.7rem' }} />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">🛏 {getRoomNum(b)}</Typography>
                        {getRoomType(b) && <Typography variant="body2" color="text.secondary">• {getRoomType(b)}</Typography>}
                      </Box>

                      {b.clientPhone && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{b.clientPhone}</Typography>}

                      <Typography variant="caption" color="text.secondary">{fmtDate(b.checkIn)} → {fmtDate(b.checkOut)}</Typography>

                      {b.rateType === 'negotiated' && <Chip label={t.negotiated} size="small" variant="outlined" color="warning" sx={{ ml: 1, fontSize: '0.65rem' }} />}

                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{displayAmt.toLocaleString()} {currency}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          {paid > 0 && <Typography variant="caption" sx={{ color: '#4caf50' }}>{t.paid}: {paid.toLocaleString()} {currency}</Typography>}
                          {remain > 0 && <Typography variant="caption" sx={{ color: '#f44336' }}>{t.remaining}: {remain.toLocaleString()} {currency}</Typography>}
                        </Box>
                        {displayAmt > 0 && (
                          <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.5, height: 4, borderRadius: 2, backgroundColor: '#f5f5f5', '& .MuiLinearProgress-bar': { backgroundColor: remain > 0 ? '#ff9800' : '#4caf50', borderRadius: 2 } }} />
                        )}
                      </Box>

                      {b.paymentMethod && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label={fmtPayment(b.paymentMethod)} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ACTION DIALOG */}
      <Dialog open={openActions} onClose={() => setOpenActions(false)} maxWidth="sm" fullWidth>
        {selectedBooking && (() => {
          const b = selectedBooking;
          const st = b.status;
          const displayAmt = getDisplayAmount(b);
          const paid = b.paidAmount || 0;
          const remain = b.remainingAmount || 0;
          return (
            <>
              <DialogTitle sx={{ fontWeight: 600 }}>{b.clientName}</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {b.nomFemme && <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t.wife} {b.nomFemme}</Typography>}
                  <Grid container spacing={2}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.room}</Typography><Typography variant="body2">{getRoomNum(b)} {getRoomType(b) ? `• ${getRoomType(b)}` : ''}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.phone}</Typography><Typography variant="body2">{b.clientPhone || '-'}</Typography></Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.checkIn}</Typography><Typography variant="body2">{fmtDate(b.checkIn)}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.checkOut}</Typography><Typography variant="body2">{fmtDate(b.checkOut)}</Typography></Grid>
                  </Grid>
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}><Typography variant="caption" color="text.secondary">{t.amount}</Typography><Typography variant="body1" sx={{ fontWeight: 600 }}>{displayAmt.toLocaleString()} {currency}</Typography></Grid>
                      <Grid item xs={4}><Typography variant="caption" color="text.secondary">{t.paid}</Typography><Typography variant="body1" sx={{ fontWeight: 600, color: '#4caf50' }}>{paid.toLocaleString()} {currency}</Typography></Grid>
                      <Grid item xs={4}><Typography variant="caption" color="text.secondary">{t.remaining}</Typography><Typography variant="body1" sx={{ fontWeight: 600, color: remain > 0 ? '#f44336' : 'inherit' }}>{remain.toLocaleString()} {currency}</Typography></Grid>
                    </Grid>
                    {b.paymentMethod && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>{fmtPayment(b.paymentMethod)}</Typography>}
                  </Box>
                  {b.notes && <Box><Typography variant="caption" color="text.secondary">Notes</Typography><Typography variant="body2">{b.notes}</Typography></Box>}

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {st === 'pending' && (
                      <>
                        <Button variant="contained" color="success" size="small" startIcon={<LogIn size={16} />} onClick={() => handleCheckIn(b._id)} disabled={actionLoading} sx={{ textTransform: 'none' }}>{t.doCheckIn}</Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<XIcon size={16} />} onClick={() => handleCancel(b._id)} disabled={actionLoading} sx={{ textTransform: 'none' }}>{t.doCancel}</Button>
                      </>
                    )}
                    {st === 'checked-in' && (
                      <>
                        <Button variant="contained" color="warning" size="small" startIcon={<LogOutIcon size={16} />} onClick={() => handleCheckOut(b._id)} disabled={actionLoading} sx={{ textTransform: 'none' }}>{t.doCheckOut}</Button>
                        <Button variant="contained" size="small" startIcon={<DollarSign size={16} />} onClick={() => { setPayAmount(''); setPayMethod('cash'); setOpenPay(true); }} disabled={actionLoading || remain <= 0} sx={{ textTransform: 'none', backgroundColor: '#0D3C4C', '&:hover': { backgroundColor: '#164B61' } }}>{t.doPay}</Button>
                      </>
                    )}
                    {st === 'checked-out' && (
                      <Button variant="contained" size="small" startIcon={<FileText size={16} />} onClick={() => handleGenerateInvoice(b._id)} disabled={actionLoading} sx={{ textTransform: 'none', backgroundColor: '#0D3C4C', '&:hover': { backgroundColor: '#164B61' } }}>{t.generateInvoice}</Button>
                    )}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenActions(false)} sx={{ textTransform: 'none' }}>{t.close}</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* PAYMENT DIALOG */}
      <Dialog open={openPay} onClose={() => setOpenPay(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t.payTitle}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label={t.payAmount} type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} size="small"
              InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }}
              helperText={selectedBooking ? `Max: ${(selectedBooking.remainingAmount || 0).toLocaleString()} ${currency}` : ''}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t.payMethod}</InputLabel>
              <Select value={payMethod} label={t.payMethod} onChange={e => setPayMethod(e.target.value)}>
                <MenuItem value="cash">{t.cash}</MenuItem>
                <MenuItem value="mobile_money">{t.mobile_money}</MenuItem>
                <MenuItem value="bank_transfer">{t.bank_transfer}</MenuItem>
                <MenuItem value="card">{t.card}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenPay(false)} sx={{ textTransform: 'none' }}>{t.cancel}</Button>
          <Button variant="contained" onClick={handlePay}
            disabled={actionLoading || !payAmount || Number(payAmount) <= 0 || Number(payAmount) > (selectedBooking?.remainingAmount || 0)}
            sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>
            {t.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Bookings;